/**
 * MijiaDeviceCard — 米家 IoT 设备卡片组件
 * 空调 (Thermostat) / 温度计 (Thermometer) 两种模式
 * 
 * 设计语言：xiaofeng-os 暗色玻璃态 + 冰蓝色 IoT 标识色系
 * 动效：motion/react 指数缓动，空调流体动画，温度计呼吸光效
 */

import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Fan, ThermometerSun, Droplets, Battery, Power, ChevronUp, ChevronDown, Wifi, WifiOff, EyeOff } from 'lucide-react';

// ── Types ──

export type DeviceType = 'air_conditioner' | 'thermometer';

export interface AirConditionerState {
  power: boolean;
  currentTemp: number;
  targetTemp: number;
  mode: 'cool' | 'heat' | 'fan' | 'auto' | 'dry';
  fanSpeed: 'auto' | 'low' | 'medium' | 'high';
}

export interface ThermometerState {
  temperature: number;
  humidity: number;
  battery: number;
}

export interface MijiaDevice {
  id: string;
  name: string;
  room: string;
  type: DeviceType;
  online: boolean;
  state: AirConditionerState | ThermometerState;
}

// ── Helpers ──

const MODE_LABELS: Record<string, string> = {
  cool: '制冷',
  heat: '制热',
  fan: '送风',
  auto: '自动',
  dry: '除湿',
};

const MODE_ICONS: Record<string, string> = {
  cool: '❄️',
  heat: '🔥',
  fan: '💨',
  auto: '🔄',
  dry: '💧',
};

const FAN_SPEED_LABELS: Record<string, string> = {
  auto: '自动',
  low: '低',
  medium: '中',
  high: '高',
};

function isAC(state: AirConditionerState | ThermometerState): state is AirConditionerState {
  return 'power' in state;
}

// ── Temperature Arc (空调温度弧线) ──

function TempArc({ current, target, power }: { current: number; target: number; power: boolean }) {
  const radius = 52;
  const strokeW = 6;
  const center = 60;
  const circumference = 2 * Math.PI * radius;

  // Map 16-30°C to 0-100%
  const minT = 16, maxT = 30;
  const pct = Math.max(0, Math.min(1, (current - minT) / (maxT - minT)));
  const dashOffset = circumference * (1 - pct);

  const targetPct = Math.max(0, Math.min(1, (target - minT) / (maxT - minT)));
  const targetAngle = targetPct * 270 - 135; // -135 to 135 degrees
  const targetRad = (targetAngle * Math.PI) / 180;
  const targetX = center + radius * 0.78 * Math.cos(targetRad);
  const targetY = center + radius * 0.78 * Math.sin(targetRad);

  const color = !power
    ? 'oklch(50% 0.02 240)'
    : current > target + 1
      ? 'oklch(60% 0.14 20)'  // warm — cooling needed
      : current < target - 1
        ? 'oklch(60% 0.14 200)' // cool — heating needed
        : 'oklch(60% 0.14 160)'; // comfortable

  const glowColor = !power
    ? 'oklch(50% 0.01 240 / 0)'
    : current > target + 1
      ? 'oklch(60% 0.14 20 / 0.25)'
      : current < target - 1
        ? 'oklch(60% 0.14 200 / 0.25)'
        : 'oklch(60% 0.14 160 / 0.25)';

  return (
    <div className="relative flex items-center justify-center" style={{ width: 120, height: 120 }}>
      {/* Glow ring */}
      <svg
        width={120}
        height={120}
        className="absolute inset-0"
        style={{ filter: `drop-shadow(0 0 8px ${glowColor})` }}
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="oklch(100% 0 0 / 0.06)"
          strokeWidth={strokeW}
        />
        <motion.circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeW}
          strokeLinecap="round"
          strokeDasharray={circumference}
          animate={{ strokeDashoffset: dashOffset }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          transform={`rotate(-135 ${center} ${center})`}
        />
      </svg>

      {/* Center temperature */}
      <motion.div
        className="absolute flex flex-col items-center"
        animate={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.span
          className="text-3xl font-bold tracking-tighter"
          style={{ color }}
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          key={current}
        >
          {current.toFixed(1)}°
        </motion.span>
        <span className="text-[11px] text-white/35 mt-0.5 font-medium">
          目标 {target}°
        </span>
      </motion.div>

      {/* Target indicator dot */}
      {power && (
        <motion.div
          className="absolute w-2 h-2 rounded-full bg-white/80"
          style={{
            left: targetX - 4,
            top: targetY - 4,
            boxShadow: '0 0 6px rgba(255,255,255,0.4)',
          }}
          animate={{ scale: [1, 1.4, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
    </div>
  );
}

// ── AC Control Buttons ──

function TempStepper({
  value,
  onUp,
  onDown,
  disabled,
}: {
  value: number;
  onUp: () => void;
  onDown: () => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <button
        onClick={onUp}
        disabled={disabled || value >= 30}
        className="p-1 rounded-md text-white/50 hover:text-white/80 hover:bg-white/10 
                   disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
      >
        <ChevronUp size={18} />
      </button>
      <span className="text-[13px] font-mono font-bold text-white/55 w-8 text-center select-none">
        {value}°
      </span>
      <button
        onClick={onDown}
        disabled={disabled || value <= 16}
        className="p-1 rounded-md text-white/50 hover:text-white/80 hover:bg-white/10 
                   disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
      >
        <ChevronDown size={18} />
      </button>
    </div>
  );
}

function ModeSelector({
  mode,
  onChange,
  disabled,
}: {
  mode: AirConditionerState['mode'];
  onChange: (m: AirConditionerState['mode']) => void;
  disabled: boolean;
}) {
  const modes: AirConditionerState['mode'][] = ['cool', 'heat', 'fan', 'auto', 'dry'];
  return (
    <div className="flex gap-1 flex-wrap">
      {modes.map((m) => (
        <button
          key={m}
          onClick={() => onChange(m)}
          disabled={disabled}
          className={`text-[11px] px-2 py-1 rounded-lg font-medium transition-all duration-200
            ${m === mode
              ? 'bg-white/10 text-white/80 ring-1 ring-white/10'
              : 'text-white/35 hover:text-white/55 hover:bg-white/5'}
            disabled:opacity-30 disabled:cursor-not-allowed`}
        >
          {MODE_ICONS[m]} {MODE_LABELS[m]}
        </button>
      ))}
    </div>
  );
}

function FanSpeedSelector({
  speed,
  onChange,
  disabled,
}: {
  speed: AirConditionerState['fanSpeed'];
  onChange: (s: AirConditionerState['fanSpeed']) => void;
  disabled: boolean;
}) {
  const speeds: AirConditionerState['fanSpeed'][] = ['auto', 'low', 'medium', 'high'];
  const bars = { auto: 0, low: 1, medium: 2, high: 3 };

  return (
    <div className="flex items-center gap-2">
      <Fan size={13} className="text-white/40" />
      <div className="flex gap-1">
        {speeds.map((s) => {
          const active = bars[s] <= bars[speed] && speed !== 'auto' && s !== 'auto';
          const isAuto = s === 'auto' && speed === 'auto';
          return (
            <button
              key={s}
              onClick={() => onChange(s)}
              disabled={disabled}
              className={`text-[11px] px-1.5 py-0.5 rounded font-medium transition-all duration-200
                ${isAuto || active
                  ? 'bg-white/10 text-white/75'
                  : 'text-white/25 hover:text-white/45 hover:bg-white/5'}
                disabled:opacity-30 disabled:cursor-not-allowed`}
              title={FAN_SPEED_LABELS[s]}
            >
              {isAuto ? 'A' : bars[s]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Thermometer Display (温度计卡片) ──

function ThermometerDisplay({ state }: { state: ThermometerState }) {
  const tempColor =
    state.temperature < 10 ? 'oklch(65% 0.14 240)' :
    state.temperature < 20 ? 'oklch(65% 0.14 210)' :
    state.temperature < 28 ? 'oklch(65% 0.14 160)' :
    state.temperature < 35 ? 'oklch(65% 0.14 40)' :
    'oklch(65% 0.14 20)';

  const batteryColor =
    state.battery > 60 ? 'oklch(60% 0.14 160)' :
    state.battery > 20 ? 'oklch(60% 0.14 60)' :
    'oklch(60% 0.14 20)';

  return (
    <div className="flex items-center gap-4">
      {/* Temperature display */}
      <div className="flex flex-col items-center min-w-[70px]">
        <motion.div
          className="relative"
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        >
          <span
            className="text-3xl font-bold tracking-tighter"
            style={{ color: tempColor }}
          >
            {state.temperature.toFixed(1)}°
          </span>
        </motion.div>
        <span className="text-[11px] text-white/35 mt-0.5">温度</span>
      </div>

      {/* Divider */}
      <div className="w-px h-10 bg-white/8" />

      {/* Humidity + Battery */}
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-1.5">
          <Droplets size={13} className="text-cyan-400/60" />
          <span className="text-sm font-mono text-white/60">{state.humidity}%</span>
          <span className="text-[11px] text-white/35">湿度</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Battery size={13} style={{ color: batteryColor }} />
          <span className="text-sm font-mono text-white/60">{state.battery}%</span>
          <span className="text-[11px] text-white/35">电量</span>
        </div>
      </div>
    </div>
  );
}

// ── Main Card Component ──

interface MijiaDeviceCardProps {
  device: MijiaDevice;
  onStateChange?: (id: string, newState: AirConditionerState | ThermometerState) => void;
  onTogglePower?: (id: string, powerOn: boolean) => void;
  onHide?: (id: string) => void;
  compact?: boolean;
}

export function MijiaDeviceCard({ device, onStateChange, onTogglePower, onHide, compact = false }: MijiaDeviceCardProps) {
  const { id, name, room, type, online, state } = device;

  const handleTogglePower = useCallback(() => {
    if (onTogglePower && isAC(state)) {
      onTogglePower(id, !(state as AirConditionerState).power);
    }
  }, [id, state, onTogglePower]);

  const handleModeChange = useCallback(
    (mode: AirConditionerState['mode']) => {
      if (isAC(state) && onStateChange) {
        onStateChange(id, { ...state, mode });
      }
    },
    [id, state, onStateChange],
  );

  const handleFanSpeedChange = useCallback(
    (fanSpeed: AirConditionerState['fanSpeed']) => {
      if (isAC(state) && onStateChange) {
        onStateChange(id, { ...state, fanSpeed });
      }
    },
    [id, state, onStateChange],
  );

  const handleTargetTemp = useCallback(
    (delta: number) => {
      if (isAC(state) && onStateChange) {
        const newTarget = Math.max(16, Math.min(30, state.targetTemp + delta));
        onStateChange(id, { ...state, targetTemp: newTarget });
      }
    },
    [id, state, onStateChange],
  );

  const isAcDevice = type === 'air_conditioner';
  const acState = isAcDevice && isAC(state) ? state : null;

  return (
    <motion.div
      className="relative rounded-2xl border border-white/[0.06] bg-white/[0.03] backdrop-blur-2xl p-4
                 transition-colors duration-300 hover:bg-white/[0.05] hover:border-white/[0.1]"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* Top bar: room + online */}
      <div className="flex items-center justify-between mb-3 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0 flex-shrink">
          <span className="text-[10px] font-bold uppercase tracking-widest text-white/35 whitespace-nowrap">
            {room}
          </span>
          <span className="w-0.5 h-0.5 rounded-full bg-white/12 shrink-0" />
          <span className="text-[11px] font-medium text-white/55 truncate max-w-[90px]">
            {name}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-auto">
          {onHide && (
            <button
              onClick={() => onHide(id)}
              className="relative z-10 p-0.5 rounded-md text-white/15 hover:text-white/50 hover:bg-white/8 transition-all duration-150"
              title="隐藏此设备"
            >
              <EyeOff size={11} />
            </button>
          )}
          {online ? (
            <Wifi size={11} className="text-green-400/50" />
          ) : (
            <WifiOff size={11} className="text-red-400/40" />
          )}
          <span className={`text-[9px] font-medium whitespace-nowrap ${online ? 'text-green-400/50' : 'text-red-400/40'}`}>
            {online ? '在线' : '离线'}
          </span>
        </div>
      </div>

      {/* AC Mode */}
      {acState && (
        compact ? (
          /* Compact: vertical layout — arc on top, controls below */
          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center justify-between w-full">
              <button
                onClick={handleTogglePower}
                disabled={!online}
                className={`p-1.5 rounded-lg transition-all duration-200
                  ${acState.power
                    ? 'bg-white/10 text-white/85 shadow-[0_0_8px_rgba(100,200,255,0.12)]'
                    : 'bg-white/5 text-white/30 hover:bg-white/8 hover:text-white/50'}
                  disabled:opacity-30 disabled:cursor-not-allowed`}
              >
                <Power size={14} />
              </button>
              <span className="text-[10px] font-medium text-white/40">
                {acState.power ? MODE_LABELS[acState.mode] : '已关机'}
              </span>
            </div>
            <TempArc current={acState.currentTemp} target={acState.targetTemp} power={acState.power} />
            <div className="flex items-center gap-2 w-full justify-center">
              <FanSpeedSelector speed={acState.fanSpeed} onChange={handleFanSpeedChange} disabled={!acState.power || !online} />
              <div className="w-px h-4 bg-white/8" />
              <TempStepper value={acState.targetTemp} onUp={() => handleTargetTemp(1)} onDown={() => handleTargetTemp(-1)} disabled={!acState.power || !online} />
            </div>
          </div>
        ) : (
          /* Full: side-by-side layout */
          <div className="flex items-stretch gap-3">
            {/* Left: Temp Arc */}
            <TempArc current={acState.currentTemp} target={acState.targetTemp} power={acState.power} />

            {/* Right: Controls */}
            <div className="flex-1 flex flex-col justify-between gap-2 min-w-0">
              {/* Power + Mode row */}
              <div className="flex items-center justify-between">
                <button
                  onClick={handleTogglePower}
                  disabled={!online}
                  className={`p-2 rounded-xl transition-all duration-200
                    ${acState.power
                      ? 'bg-white/10 text-white/85 shadow-[0_0_12px_rgba(100,200,255,0.15)]'
                      : 'bg-white/5 text-white/30 hover:bg-white/8 hover:text-white/50'}
                    disabled:opacity-30 disabled:cursor-not-allowed`}
                >
                  <Power size={16} />
                </button>

                <div className="flex items-center gap-2">
                  <ModeSelector mode={acState.mode} onChange={handleModeChange} disabled={!acState.power || !online} />
                </div>
              </div>

              {/* Temp stepper */}
              <div className="flex items-center gap-4">
                <FanSpeedSelector speed={acState.fanSpeed} onChange={handleFanSpeedChange} disabled={!acState.power || !online} />
                <div className="ml-auto">
                  <TempStepper value={acState.targetTemp} onUp={() => handleTargetTemp(1)} onDown={() => handleTargetTemp(-1)} disabled={!acState.power || !online} />
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {/* Thermometer Mode */}
      {!isAcDevice && (
        <ThermometerDisplay state={state as ThermometerState} />
      )}

      {/* Offline overlay */}
      {!online && (
        <div className="absolute inset-0 rounded-2xl bg-black/20 backdrop-blur-[2px] flex items-center justify-center">
          <span className="text-xs font-bold text-white/30 tracking-wider uppercase">离线</span>
        </div>
      )}
    </motion.div>
  );
}
